/**
 * The contract between the cogent widget and the character it shows, kept
 * apart from the renderer so the widget loads without three.js.
 */

import type { Expression } from "./expressions";
import type { Look } from "./wardrobe";

/** The clips a hover or focus picks from at random; at rest the character plays `idle`. */
export const HOVER_CLIPS = [
  "jog",
  "spell",
  "dance",
  "sword",
  "interact",
] as const;
/** Played while it is carried or glides: falling through the air, then landing in its corner. */
export const MOTION_CLIPS = ["fall", "land"] as const;
/** A hover clip, or a motion clip while it is moved. */
export type Clip = (typeof HOVER_CLIPS)[number] | (typeof MOTION_CLIPS)[number];

export type CogentCharacterProps = {
  cogentId: string;
  name: string;
  /** The wardrobe's folder: `wardrobe.json`, the rig, and the parts and clips it lists. */
  assetsBaseUrl: string;
  /** What it wears; null for the starter look picked by its id. A new look swaps parts in place. */
  look: Look | null;
  /** The face it makes over its look (its Eyes, Brow and Mouth swapped in place); null for its own. */
  expression: Expression | null;
  state: "idle" | "hover";
  /**
   * What the cogent is doing, from its real status (optional; a character may ignore it): thinking (a request is
   * waiting on it), working (a step of a task, `step` its text), speaking (its reply streaming in, `said` its length
   * so far in characters), else idle.
   */
  activity?: CogentActivity;
  clip: Clip | null;
  /** The way it turns: toward the page's center, so "right" in a left corner. */
  facing: "left" | "right";
  /** Called when a hover clip has played through, so the host returns to idle. */
  onClipEnd: () => void;
  /** Where the standing figure is drawn, in CSS px from the canvas's top-left; sent on load, resize and turn. */
  onFigure: (figure: Figure) => void;
  /** A stand-in, not yet anyone's: drawn in greys, faded toward the paper. */
  muted?: boolean;
  /** Called once it is drawn ("ready"), or when its load failed ("failed"); a new `key` loads it again. */
  onLoad?: (state: "ready" | "failed") => void;
};

export type CogentActivity = {
  kind: "idle" | "thinking" | "working" | "speaking";
  step: string | null;
  said: number;
};

export type Box = { left: number; top: number; right: number; bottom: number };
/** The standing figure, its head, and its mouth's height: where a speech bubble's tail aims. */
export type Figure = Box & { head: Box; mouthY: number };
